import { useEffect, useState } from 'react';

import { RuntimeService, type CurrentSite } from '../../../services/RuntimeService';

/**
 * 30초마다 manage-backend 의 /v0.1/runtime/current-site 를 폴링.
 * - 짧은 주기(<10s) 는 docker socket inspect 호출이 누적되어 부담
 * - 너무 긴 주기(>1m) 는 사이트 교체 직후 표시가 묵음
 *
 * 페이지 visibility hidden 동안에는 폴링 정지(불필요한 socket 호출 절약).
 */
const POLL_MS = 30_000;

export interface UseCurrentSiteResult {
  data: CurrentSite | null;
  loading: boolean;
  error: string | null;
}

export default function useCurrentSite(): UseCurrentSiteResult {
  const [data, setData] = useState<CurrentSite | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchOnce = async () => {
      try {
        const next = await RuntimeService.getCurrentSite();
        if (!cancelled) {
          setData(next);
          setError(null);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'unknown error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchOnce();

    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchOnce();
      }
    }, POLL_MS);

    // 탭이 다시 활성화될 때 즉시 한 번 갱신
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchOnce();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return { data, loading, error };
}
